import React, { useState } from "react";
import PropTypes from "prop-types";

import ListarDatosPaginados from "../../general/ListarDatosPaginados";
import { usePaginacion } from "../../general/Paginacion";
import { BtnDetalle, BtnGestionar } from "../../general/BotonesAccion";
import FiltrosSolicitudes from "../components/FiltrosSolicitudes";
import { Cargando, EstadoVacio } from "../components/Estados";

import { useConsulta } from "../hooks/useConsulta";
import { useDebounce } from "../hooks/useDebounce";
import {
  exportarSolicitudesGestion,
  listarSolicitudesGestion,
  obtenerEstadosGestion,
} from "../services/bibliotecaApi";
import {
  COLUMNAS_GESTION,
  ESTADOS_POR_DEFECTO,
  colorDeEstado,
  filaPrincipalGestion,
  tituloConEstados,
} from "../model/gestion";
import { SOLICITUDES, buscarPorCodigo } from "../catalogo";
import { descargarExcel } from "./excel";
import ModalDetalleGestion from "./ModalDetalleGestion";
import ModalGestionarSolicitud from "./ModalGestionarSolicitud";

/**
 * Las solicitudes de cualquier solicitante que el gestor pueda
 * ver, en la tabla de Gestion Practicas: buscar, descargar a Excel y
 * refrescar. Cada fila trae, como alla, el libro para abrir el detalle en un
 * modal y, si quien la ve puede hacer algo con ella (`gestionable`, lo
 * decide el backend), el boton para gestionarla en otro modal.
 *
 * Los estados y el periodo se eligen en los selectores de arriba; el de
 * estados dice cuantas solicitudes hay en cada uno, lo que antes mostraban
 * las tarjetas de estados, y arranca en las que siguen en curso.
 *
 * Esta paginada en el servidor: se trae solo la pagina que se ve. Por eso
 * buscar tambien lo hace el servidor (sobre todas, no sobre la pagina) y el
 * Excel pide aparte todas las que cumplen los filtros.
 */
const TODOS = "";

const ListaGestion = ({ periodos, onMensaje }) => {
  // Arranca con los dos tipos; el selector deja ver uno solo.
  const [tipo, setTipo] = useState(TODOS);
  const nombreTipo = tipo ? buscarPorCodigo(SOLICITUDES, tipo)?.nombre ?? "" : "Solicitudes";
  const paginacion = usePaginacion();
  const { pagina, porPagina } = paginacion;
  const [estado, setEstado] = useState(ESTADOS_POR_DEFECTO);
  // Sin elegir, el periodo vigente.
  const [periodo, setPeriodo] = useState(periodos.vigente ?? "");
  const [texto, setTexto] = useState("");
  // Se consulta cuando se deja de escribir, no en cada tecla.
  const buscar = useDebounce(texto.trim(), 400);
  // Cambia al refrescar o al gestionar: la tabla y los conteos se vuelven a
  // consultar.
  const [version, setVersion] = useState(0);
  const [descargando, setDescargando] = useState(false);
  const [abierta, setAbierta] = useState(null);
  const [gestionando, setGestionando] = useState(null);

  const filtros = { tipo, periodo, buscar, estado };

  // Mientras llega una pagina nueva, `datos` conserva la anterior: la lista
  // no desaparece, y el buscador no pierde lo que se esta escribiendo.
  const { datos, cargando, error } = useConsulta(
    () => listarSolicitudesGestion(filtros, { pagina, porPagina }),
    [tipo, estado, periodo, buscar, pagina, porPagina, version],
    null,
  );

  // Los estados del tipo (o de los dos) con su conteo, para el selector.
  const { datos: conteo } = useConsulta(
    () => obtenerEstadosGestion(tipo, periodo),
    [tipo, periodo, version],
    null,
  );

  // Cambiar un filtro vuelve a la primera pagina.
  const filtrar = (cambiar) => (valor) => {
    cambiar(valor);
    paginacion.reiniciar();
  };

  const descargar = () => {
    if (descargando) return;
    setDescargando(true);
    exportarSolicitudesGestion(filtros)
      .then((solicitudes) => {
        if (solicitudes.length === 0) {
          onMensaje("No hay solicitudes para descargar.");
          return;
        }
        descargarExcel(solicitudes, "EMMA - BIBLIOTECA");
      })
      .catch((fallo) => onMensaje(fallo.message, "error"))
      .finally(() => setDescargando(false));
  };

  const titulo = tituloConEstados(nombreTipo, conteo?.estados ?? [], estado);

  const contenido = () => {
    // Solo la primera carga ocupa la pantalla entera.
    if (cargando && !datos) return <Cargando />;
    if (error && !datos) {
      return <EstadoVacio mensaje={error.message ?? "No fue posible cargar las solicitudes"} />;
    }

    return (
      <ListarDatosPaginados
        datos={datos?.solicitudes ?? []}
        total={datos?.total ?? 0}
        {...paginacion}
        cargando={cargando}
        titulo={titulo}
        busqueda={texto}
        onBuscar={filtrar(setTexto)}
        onRefrescar={() => setVersion((actual) => actual + 1)}
        onDescargar={descargar}
        mensajeVacio={
          buscar || estado.length ? "No se encontraron solicitudes" : "No hay solicitudes en este periodo"
        }
        avatar={({ estado_actual }) => estado_actual?.nombre?.charAt(0).toUpperCase()}
        color_avatar={({ estado_actual }) => colorDeEstado(estado_actual?.codigo)}
        fila_principal={filaPrincipalGestion}
        filas={COLUMNAS_GESTION}
        acciones={(solicitud) => (
          <div>
            <BtnDetalle
              callback={() => setAbierta(solicitud.id)}
              color="#01579b"
              texto="Abrir"
            />
            {solicitud.gestionable && (
              <BtnGestionar
                callback={() => setGestionando(solicitud.id)}
                color="#00bfa5"
                texto="Gestionar"
              />
            )}
          </div>
        )}
      />
    );
  };

  return (
    <div>
      <FiltrosSolicitudes
        tipos={SOLICITUDES}
        tipo={tipo}
        onTipo={filtrar(setTipo)}
        estado={estado}
        estados={conteo?.estados ?? []}
        total={conteo ? conteo.total : null}
        onEstado={filtrar(setEstado)}
        periodo={periodo}
        periodos={periodos.periodos}
        vigente={periodos.vigente}
        onPeriodo={filtrar(setPeriodo)}
      />
      {contenido()}

      {abierta && (
        <ModalDetalleGestion
          id={abierta}
          version={version}
          onCerrar={() => setAbierta(null)}
          onGestionar={(id) => {
            setAbierta(null);
            setGestionando(id);
          }}
        />
      )}
      {gestionando && (
        <ModalGestionarSolicitud
          id={gestionando}
          onMensaje={onMensaje}
          onCerrar={() => setGestionando(null)}
          onCambio={() => setVersion((actual) => actual + 1)}
        />
      )}
    </div>
  );
};

ListaGestion.propTypes = {
  /** Lo que responde obtenerPeriodosGestion: { vigente, periodos }. */
  periodos: PropTypes.shape({
    vigente: PropTypes.string,
    periodos: PropTypes.arrayOf(PropTypes.string).isRequired,
  }).isRequired,
  onMensaje: PropTypes.func.isRequired,
};

export default ListaGestion;
